/**
 * PRIMA MENSUAL, SEMESTRAL Y ANUAL, Y CUÁL SE ENSEÑA (Lissette 2026-10-22).
 *
 * "En todas las fichas de pólizas, menos Medicare y Salud, colocar mensual,
 * semestral y anual; que en Comisiones, al sacar el total a pagar, el
 * porcentaje tome de la que esté llena; y que en la ficha podamos definir cuál
 * es la predeterminada para la vista previa."
 *
 * - Las tres viven donde ya vivían: la mensual en la columna `premium`
 *   (`monthlyPremium` en pantalla) y la anual y la semestral en `typeDetails`
 *   (`premiumAnnual`, `premiumSemiAnnual`, que ya usaba P&C). La elegida va en
 *   `typeDetails.premiumDefault`. Sin migración.
 * - NO llevan las tres: Medicare (ma, medsup, partd), Salud (aca) y Vida (life,
 *   final — "no toques la de vida"). Corto plazo SÍ las lleva ("no cuenta
 *   como salud"). En esos tipos todo sigue con la mensual, como siempre.
 *
 * Sin React, para poder probarlo.
 */
import { esPolizaDeSalud } from './policyTypes.js';

export const PERIODOS_DE_PRIMA = ['monthly', 'semiannual', 'annual'];
const CAMPO = { monthly: 'monthlyPremium', semiannual: 'premiumSemiAnnual', annual: 'premiumAnnual' };
const SIN_TRES = new Set(['ma', 'medsup', 'partd', 'life', 'final']);

/** ¿Este tipo de póliza lleva las tres primas? */
export function llevaTresPrimas(type) {
  const t = String(type || '');
  return !!t && !SIN_TRES.has(t) && !esPolizaDeSalud(t);
}

const numero = (v) => Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
const monto = (v) => {
  const n = numero(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
/** ¿El campo tiene un monto escrito? Un 0 escrito CUENTA como lleno: se enseña "$0". */
const estaLlena = (v) => String(v ?? '').trim() !== '' && Number.isFinite(numero(v)) && numero(v) >= 0;

/**
 * La prima que cuenta: { monto, periodo, llena }. Manda lo que la agente
 * eligió, y un $0 escrito es un dato, no un hueco (Lissette 2026-10-23: "yo
 * decido cuál colocar; si está en 0 déjala en 0 a menos que yo diga cuál toma"):
 *   - la elegida (`premiumDefault`) si está escrita, AUNQUE sea 0;
 *   - si no eligió (o la elegida está vacía): la primera escrita en el orden
 *     mensual → semestral → anual, aunque sea 0. Un 0 NO se salta para buscar
 *     otra con monto;
 *   - en los tipos sin las tres, siempre la mensual.
 * `llena` false = ninguna escrita (no hay nada que enseñar).
 */
export function primaElegida(poliza) {
  if (!poliza || !llevaTresPrimas(poliza.type)) {
    return { monto: monto(poliza?.monthlyPremium), periodo: 'monthly', llena: estaLlena(poliza?.monthlyPremium) };
  }
  const elegida = PERIODOS_DE_PRIMA.includes(poliza.premiumDefault) ? poliza.premiumDefault : null;
  const periodo = [elegida, ...PERIODOS_DE_PRIMA].find((p) => p && estaLlena(poliza[CAMPO[p]]));
  if (periodo) return { monto: monto(poliza[CAMPO[periodo]]), periodo, llena: true };
  return { monto: 0, periodo: elegida || 'monthly', llena: false };
}

/** Lo que se lee junto al monto: "/mo", "/6m", "/año". */
export function sufijoDePeriodo(periodo, lang = 'es') {
  const es = lang === 'es';
  if (periodo === 'annual') return es ? '/año' : '/yr';
  if (periodo === 'semiannual') return es ? '/6 meses' : '/6 mo';
  return '/mo';
}

export function nombreDePeriodo(periodo, lang = 'es') {
  const es = lang === 'es';
  if (periodo === 'annual') return es ? 'Anual' : 'Annual';
  if (periodo === 'semiannual') return es ? 'Semestral' : 'Semi-annual';
  return es ? 'Mensual' : 'Monthly';
}
